import { addDays, assertCalendarDate, calendarDate, isValidCalendarDate, toIsoDateString, } from './calendar-date.js';
/** Shape check only: ten characters, `YYYY-MM-DD`, digits where digits belong. */
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Lowest year expressible in the four-digit `YYYY-MM-DD` form. */
const MIN_PLAIN_DATE_YEAR = 0;
/** Highest year expressible in the four-digit `YYYY-MM-DD` form. */
const MAX_PLAIN_DATE_YEAR = 9999;
/**
 * `YYYY-MM-DD` text to a validated {@link CalendarDate}, or `undefined` when the
 * text is not one.
 *
 * The one parser. Everything public in this module is a thin wrapper over it,
 * which is what keeps "is this a date" and "what date is it" from being answered
 * by two different pieces of code.
 */
function parseIsoDateText(text) {
    const match = ISO_DATE_PATTERN.exec(text);
    if (match === null) {
        return undefined;
    }
    // The capture groups are non-optional in the pattern, so the fallbacks are for
    // `noUncheckedIndexedAccess` only and are never taken.
    const candidate = {
        year: Number(match[1] ?? ''),
        month: Number(match[2] ?? ''),
        day: Number(match[3] ?? ''),
    };
    return isValidCalendarDate(candidate) ? candidate : undefined;
}
/**
 * Whether `value` is a well-formed calendar date in the `PlainDate` shape.
 *
 * Rejects the shape violations (`'2025-1-5'`, `'20250105'`) and the impossible
 * days (`'2025-02-30'`, `'2025-13-01'`) alike, so a value that passes is safe to
 * compare lexicographically.
 */
export function isPlainDate(value) {
    return parseIsoDateText(value) !== undefined;
}
/**
 * Narrows a string to {@link PlainDate}, throwing on anything that is not a real
 * calendar day.
 *
 * Throwing is right at this boundary: a malformed date reaching an interval
 * predicate would silently produce a wrong Sales_Cycle set, and one reaching the
 * calendar geometry would silently draw a bar in the wrong place. Both are worse
 * than a loud failure at the point the value entered the domain.
 */
export function plainDate(value) {
    if (!isPlainDate(value)) {
        throw new RangeError(`Not an ISO 8601 calendar date (YYYY-MM-DD): ${JSON.stringify(value)}`);
    }
    return value;
}
/**
 * Parses ISO 8601 `YYYY-MM-DD` text into a validated {@link CalendarDate}.
 *
 * The entry point for date text that has not been branded yet, used wherever the
 * calendar area receives a raw string. Prefer {@link toCalendarDate} when the
 * value is already a {@link PlainDate}: it says in its type that the parse
 * cannot fail.
 */
export function parseCalendarDate(text) {
    const parsed = parseIsoDateText(text);
    if (parsed === undefined) {
        throw new RangeError(`Not an ISO 8601 calendar date (YYYY-MM-DD): ${JSON.stringify(text)}`);
    }
    return parsed;
}
/**
 * A {@link PlainDate} as a {@link CalendarDate}, for arithmetic.
 *
 * Cannot fail for a branded value: the brand is only obtainable from
 * {@link plainDate} or {@link toPlainDate}, both of which validate.
 */
export function toCalendarDate(date) {
    return parseCalendarDate(date);
}
/**
 * A {@link CalendarDate} as a {@link PlainDate}, for storage, comparison and the
 * snapshot.
 *
 * Owns the four-digit-year rule: the `YYYY-MM-DD` form cannot express a year
 * outside 0-9999, so a date that has walked out of that range through
 * {@link addDays} fails here rather than being padded into a lie. That is what
 * makes {@link nextDay} throw at 9999-12-31 and {@link previousDay} throw at
 * 0000-01-01.
 */
export function toPlainDate(date) {
    const { year } = assertCalendarDate(date);
    if (year < MIN_PLAIN_DATE_YEAR || year > MAX_PLAIN_DATE_YEAR) {
        throw new RangeError(`Year outside the four-digit range: ${String(year)}`);
    }
    return plainDate(toIsoDateString(date));
}
/** Builds a {@link PlainDate} from numeric parts, validating the calendar day. */
export function plainDateFromParts(year, month, day) {
    if (!Number.isInteger(month) || !Number.isInteger(day)) {
        throw new RangeError(`Month and day must be integers: ${String(month)}, ${String(day)}`);
    }
    return toPlainDate(calendarDate(year, month, day));
}
/** The year, month and day of a {@link PlainDate}. */
export function plainDateParts(date) {
    return toCalendarDate(date);
}
/** The calendar year of a {@link PlainDate}. */
export function plainDateYear(date) {
    return plainDateParts(date).year;
}
/**
 * Calendar comparison: negative when `a` is earlier, zero when the same day,
 * positive when `a` is later.
 *
 * Fixed-width zero-padded ISO dates make string order calendar order, so this is
 * a string comparison by construction rather than by coincidence.
 */
export function comparePlainDates(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** `a` is the same day as or earlier than `b`. */
export function isOnOrBefore(a, b) {
    return a <= b;
}
/** `a` is the same day as or later than `b`. */
export function isOnOrAfter(a, b) {
    return a >= b;
}
/**
 * The calendar day after `date`.
 *
 * Day arithmetic is exposed on this type for exactly one reason: the boundary
 * between a covered range and an uncovered one. With closed intervals on a
 * discrete calendar, the first uncovered day after a range ending on 2025-03-31
 * is 2025-04-01 - its successor - and nothing weaker than a successor can express
 * that. Gap detection (R6.5) is built on this and on {@link previousDay}.
 *
 * The rollover is {@link addDays}', not this module's: no month lengths and no
 * leap year rule appear here. Throws for 9999-12-31, which has no representable
 * successor in the `YYYY-MM-DD` form; planning years are constrained to 2000-2100
 * by the database, so that bound is unreachable in practice and is left loud
 * rather than clamped.
 */
export function nextDay(date) {
    return toPlainDate(addDays(toCalendarDate(date), 1));
}
/**
 * The calendar day before `date`.
 *
 * The mirror of {@link nextDay}: the last covered day before a range starting on
 * 2025-04-01 is 2025-03-31. Throws for 0000-01-01, which has no representable
 * predecessor.
 */
export function previousDay(date) {
    return toPlainDate(addDays(toCalendarDate(date), -1));
}
//# sourceMappingURL=plain-date.js.map